import React from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { FeeQuoteOutcome } from '../lib/fee-quote-record';

/**
 * The failed/degraded face of the Review Fees step (SPEC-SOR-343 R11) —
 * heading, corrective message and retry control, rendered INSIDE the
 * always-mounted `fee-calc-blocked` live region in place of any dollar figure.
 * Reads no context, so it renders under `renderToStaticMarkup` without
 * providers — the spec property probe `spec_sor_343proptest/gen/r11.ts` pins
 * the rendered face through it (the BlockingMessage precedent).
 */

/** The presentation state a failed or degraded attempt leaves on the step. */
export interface FeeCalcFailure {
  kind: 'failed' | 'court-rejected' | 'degraded';
  message: string;
}

/** Heading per failure kind — the WOL-462 court rejection keeps its own framing. */
const FAILURE_HEADING: Record<FeeCalcFailure['kind'], string> = {
  failed: 'Fee calculation failed',
  'court-rejected': 'The court reported a problem with this filing',
  degraded: 'We could not calculate your fees',
};

/** Degraded carries no message from the API — the step supplies the copy. */
const DEGRADED_MESSAGE = 'We couldn’t complete the fee calculation. Please try again in a moment.';

/** The failed/degraded view of the last classified outcome; resolved and unresolved render their own surfaces. */
// eslint-disable-next-line react-refresh/only-export-components -- the view mapping is this component's own contract, kept beside it
export function feeFailureView(outcome: FeeQuoteOutcome): FeeCalcFailure | null {
  switch (outcome.outcome) {
    case 'failed':
      return { kind: outcome.courtRejected ? 'court-rejected' : 'failed', message: outcome.message };
    case 'degraded':
      return { kind: 'degraded', message: DEGRADED_MESSAGE };
    default:
      return null;
  }
}

interface FeeCalcFailureBlockProps {
  failure: FeeCalcFailure;
  brand: BrandConfig;
  onRetry: () => void;
}

export const FeeCalcFailureBlock: React.FC<FeeCalcFailureBlockProps> = ({ failure, brand, onRetry }) => (
  <>
    <p className="text-sm font-semibold mb-1">{FAILURE_HEADING[failure.kind]}</p>
    <p className="text-sm mb-3">{failure.message}</p>
    <button
      type="button"
      data-testid="fee-calc-retry"
      onClick={onRetry}
      className="px-4 py-2 rounded-lg text-sm font-medium"
      style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
    >
      Retry calculation
    </button>
  </>
);
